import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import axios from 'axios';

function Reports() {
  const [reportType, setReportType] = useState('Inventory Reports');

  const [discards, setDiscards] = useState([]);
  const [giveaways, setGiveaways] = useState([]);
  const [giveawaysLoading, setGiveawaysLoading] = useState(false);

  const [giveawaySku, setGiveawaySku] = useState('');
  const [giveawayQuantity, setGiveawayQuantity] = useState(1);
  const [giveawayEventName, setGiveawayEventName] = useState('');
  const [giveawayOrderNumber, setGiveawayOrderNumber] = useState('');
  const [giveawayShippingCost, setGiveawayShippingCost] = useState('');
  const [giveawayNotes, setGiveawayNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const [editingGiveawayId, setEditingGiveawayId] = useState(null);
  const [editGiveawayOrderNumber, setEditGiveawayOrderNumber] = useState('');
  const [editGiveawayShippingCost, setEditGiveawayShippingCost] = useState('');
  const [savingGiveawayEdit, setSavingGiveawayEdit] = useState(false);

  const [fees, setFees] = useState([]);
  const [feesLoading, setFeesLoading] = useState(false);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [exportType, setExportType] = useState('inventory_costs');

  const [inventoryReport, setInventoryReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [topLimit, setTopLimit] = useState(100);

  const [totalInventoryCost, setTotalInventoryCost] = useState(null);
  const [quickBuyReport, setQuickBuyReport] = useState(null);
  const [pendingInventoryCostsReport, setPendingInventoryCostsReport] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(today);

  const pageStyle = {
    minHeight: '100vh',
    background:
      'radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)',
    color: '#e5e7eb',
  };

  const shellStyle = {
    maxWidth: 1800,
    margin: '0 auto',
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.86)',
    border: '1px solid rgba(148, 163, 184, 0.16)',
    borderRadius: 22,
    color: '#e5e7eb',
    boxShadow: '0 18px 45px rgba(0,0,0,0.28)',
    backdropFilter: 'blur(10px)',
  };

  const cardSoftStyle = {
    background: 'rgba(30, 41, 59, 0.82)',
    border: '1px solid rgba(148, 163, 184, 0.14)',
    borderRadius: 20,
    color: '#e5e7eb',
  };

  const heroStyle = {
    background:
      'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(34,197,94,0.20))',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: 28,
    color: '#f8fafc',
    boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
  };

  const inputStyle = {
    background: '#020617',
    color: '#e5e7eb',
    border: '1px solid rgba(148, 163, 184, 0.35)',
    borderRadius: 12,
  };

  const inlineInputStyle = {
    background: '#020617',
    color: '#e5e7eb',
    border: '1px solid rgba(148, 163, 184, 0.35)',
    borderRadius: 10,
    minWidth: 120,
  };

  const mutedStyle = {
    color: '#94a3b8',
  };

  const pillStyle = {
    border: '1px solid rgba(148, 163, 184, 0.20)',
    background: 'rgba(15, 23, 42, 0.72)',
    color: '#cbd5e1',
    borderRadius: 999,
    padding: '6px 11px',
    fontSize: 12,
    fontWeight: 700,
  };

  const reportOptions = [
    {
      value: 'Inventory Reports',
      label: 'Inventory Reports',
      icon: '📦',
      description: 'Inventory costs, Quick Buy value, pending inventory, and exports.',
      color: '#38bdf8',
    },
    {
      value: 'Sales',
      label: 'Sales',
      icon: '📈',
      description: 'Sales reports coming soon.',
      color: '#22c55e',
    },
    {
      value: 'Inventory',
      label: 'Inventory',
      icon: '🏷️',
      description: 'Inventory reports coming soon.',
      color: '#a855f7',
    },
    {
      value: 'Orders',
      label: 'Orders',
      icon: '🧾',
      description: 'Orders reports coming soon.',
      color: '#f59e0b',
    },
    {
      value: 'Discards',
      label: 'Discards',
      icon: '🗑️',
      description: 'Damaged, discarded, or written-off inventory.',
      color: '#ef4444',
    },
    {
      value: 'Giveaways',
      label: 'Giveaways',
      icon: '🎁',
      description: 'Promotional giveaway write-offs for accounting.',
      color: '#ec4899',
    },
    {
      value: 'eBay Fees',
      label: 'eBay Fees',
      icon: '💳',
      description: 'eBay fee transactions by date range.',
      color: '#3b82f6',
    },
  ];

  const selectedReportMeta =
    reportOptions.find((item) => item.value === reportType) || reportOptions[0];

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const money = (v) =>
    toNumber(v).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatMoney = (v) => `$${money(v)}`;

  const getMoneyColor = (value) => {
    const num = toNumber(value);
    if (num > 0) return '#22c55e';
    if (num < 0) return '#ef4444';
    return '#cbd5e1';
  };

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('accessToken');

    if (!token) return {};

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const clearLiveReports = () => {
    setInventoryReport(null);
    setTotalInventoryCost(null);
    setQuickBuyReport(null);
    setPendingInventoryCostsReport(null);
  };

  const clearMessages = () => {
    setError(null);
    setMessage(null);
  };

  const clearGiveawayEdit = () => {
    setEditingGiveawayId(null);
    setEditGiveawayOrderNumber('');
    setEditGiveawayShippingCost('');
    setSavingGiveawayEdit(false);
  };

  const liveReportLoaded =
    inventoryReport ||
    totalInventoryCost !== null ||
    quickBuyReport ||
    pendingInventoryCostsReport;

  const quickBuyProfitColor = getMoneyColor(quickBuyReport?.gross_profit_if_sold);
  const pendingProfitColor = getMoneyColor(pendingInventoryCostsReport?.gross_profit_if_sold);

  const feeTotals = useMemo(() => {
    return fees.reduce(
      (acc, fee) => {
        acc.count += 1;
        acc.amount += toNumber(fee.amount);
        return acc;
      },
      { count: 0, amount: 0 }
    );
  }, [fees]);

  const discardTotals = useMemo(() => {
    return discards.reduce(
      (acc, item) => {
        acc.count += 1;
        acc.cost += toNumber(item.cost);
        return acc;
      },
      { count: 0, cost: 0 }
    );
  }, [discards]);

  const giveawayTotals = useMemo(() => {
    return giveaways.reduce(
      (acc, item) => {
        const itemCost = toNumber(item.total_cost);
        const shippingCost = toNumber(item.shipping_cost);

        acc.count += 1;
        acc.quantity += toNumber(item.quantity);
        acc.cost += itemCost;
        acc.shipping += shippingCost;
        acc.total += itemCost + shippingCost;
        return acc;
      },
      { count: 0, quantity: 0, cost: 0, shipping: 0, total: 0 }
    );
  }, [giveaways]);

  const fetchGiveaways = async () => {
    try {
      setGiveawaysLoading(true);
      clearMessages();

      const res = await axios.get('/api/giveaways', getAuthHeaders());

      setGiveaways(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('[Reports] Fetch giveaways error:', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to fetch giveaway write-offs.'
      );
    } finally {
      setGiveawaysLoading(false);
    }
  };

  const handleAddGiveaway = async () => {
  try {
    clearMessages();

    const sku = giveawaySku.trim();

    const shippingCostRaw = String(giveawayShippingCost || '').trim();
    const shippingCost = shippingCostRaw === '' ? 0 : Number(shippingCostRaw);

    if (!Number.isFinite(shippingCost) || shippingCost < 0) {
      setError('Please enter a valid shipping cost. Use 0 if there was no shipping cost.');
      return;
    }

    if (!sku && shippingCost <= 0) {
      setError('Enter a SKU or a shipping cost.');
      return;
    }

    const qty = sku ? Math.max(1, Number(giveawayQuantity) || 1) : 0;

    const res = await axios.post(
      '/api/giveaways',
      {
        sku: sku || null,
        quantity: qty,
        reason: 'Giveaway',
        event_name: giveawayEventName.trim() || null,
        order_number: giveawayOrderNumber.trim() || null,
        shipping_cost: shippingCost,
        notes: giveawayNotes.trim() || null,
      },
      getAuthHeaders()
    );

    setMessage(res.data?.message || 'Giveaway write-off recorded.');

    setGiveawaySku('');
    setGiveawayQuantity(1);
    setGiveawayEventName('');
    setGiveawayOrderNumber('');
    setGiveawayShippingCost('');
    setGiveawayNotes('');

    fetchGiveaways();
  } catch (err) {
    console.error('[Reports] Add giveaway error:', err);
    setError(
      err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to record giveaway write-off.'
    );
  }
};
  const handleStartGiveawayEdit = (item) => {
    clearMessages();
    setEditingGiveawayId(item.id);
    setEditGiveawayOrderNumber(item.order_number || '');
    setEditGiveawayShippingCost(
      item.shipping_cost !== null && item.shipping_cost !== undefined
        ? String(toNumber(item.shipping_cost).toFixed(2))
        : '0.00'
    );
  };

  const handleSaveGiveawayEdit = async (item) => {
    try {
      clearMessages();

      const shippingCostRaw = String(editGiveawayShippingCost || '').trim();
      const shippingCost = shippingCostRaw === '' ? 0 : Number(shippingCostRaw);

      if (!Number.isFinite(shippingCost) || shippingCost < 0) {
        setError('Please enter a valid shipping cost. Use 0 if there was no shipping cost.');
        return;
      }

      setSavingGiveawayEdit(true);

      const res = await axios.put(
        `/api/giveaways/${item.id}`,
        {
          order_number: editGiveawayOrderNumber.trim() || null,
          shipping_cost: shippingCost,
        },
        getAuthHeaders()
      );

      setMessage(res.data?.message || 'Giveaway shipping/order info updated.');
      clearGiveawayEdit();
      fetchGiveaways();
    } catch (err) {
      console.error('[Reports] Update giveaway error:', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to update giveaway shipping/order info.'
      );
      setSavingGiveawayEdit(false);
    }
  };

  const handleExportGiveaways = () => {
    window.open('/api/giveaways/export', '_blank');
  };

  const handleArchiveGiveaways = async () => {
    if (!window.confirm('Archive current giveaway write-offs and reset? This cannot be undone.')) {
      return;
    }

    try {
      clearMessages();

      const res = await axios.post('/api/giveaways/archive', {}, getAuthHeaders());

      setMessage(res.data?.message || 'Giveaway write-offs archived and reset.');
      clearGiveawayEdit();
      fetchGiveaways();
    } catch (err) {
      console.error('[Reports] Archive giveaways error:', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to archive giveaway write-offs.'
      );
    }
  };

  const fetchDiscards = async () => {
    try {
      setLoading(true);
      clearMessages();

      const response = await fetch('/api/discards', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch discards');
      }

      const data = await response.json();
      setDiscards(data);
    } catch (err) {
      console.error('[Reports] Fetch discards error:', err);
      setError('Failed to fetch discarded items.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportDiscards = async () => {
    try {
      clearMessages();

      const response = await fetch('/api/discards/export', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export discards');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `discarded_items_${Date.now()}.csv`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Reports] Export error:', err);
      setError('Error exporting discarded items.');
    }
  };

  const handleArchiveDiscards = async () => {
    if (!window.confirm('Archive current discards and reset? This cannot be undone.')) {
      return;
    }

    try {
      clearMessages();

      const response = await fetch('/api/discards/archive', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to archive discards');
      }

      setMessage('Discards archived and reset.');
      fetchDiscards();
    } catch (err) {
      console.error('[Reports] Archive error:', err);
      setError('Error archiving discarded items.');
    }
  };

  const fetchEbayFees = async () => {
    try {
      setFeesLoading(true);
      clearMessages();

      const response = await fetch(`/api/ebay/fees?start=${startDate}&end=${endDate}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch eBay fees');
      }

      const data = await response.json();
      setFees(data.transactions || []);
    } catch (err) {
      console.error('[Reports] Fetch eBay Fees error:', err);
      setError('Failed to fetch eBay fees.');
    } finally {
      setFeesLoading(false);
    }
  };

  const fetchInventoryCostReport = async (limit) => {
    try {
      setLoadingReport(true);
      clearLiveReports();
      clearMessages();

      const res = await axios.get(`/api/reports/top-inventory-cost?limit=${limit}`);

      setInventoryReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load inventory cost report.');
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchTotalInventoryCost = async () => {
    try {
      setLoadingReport(true);
      clearLiveReports();
      clearMessages();

      const res = await axios.get('/api/reports/top-inventory-cost?limit=0');

      setTotalInventoryCost(res.data.total_inventory_cost);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load total inventory cost.');
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchQuickBuyReport = async () => {
    try {
      setLoadingReport(true);
      clearLiveReports();
      clearMessages();

      const res = await axios.get('/api/quick-batch/report-summary', getAuthHeaders());

      setQuickBuyReport(res.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to load Quick Buy value report.'
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const fetchPendingInventoryCosts = async () => {
    try {
      setLoadingReport(true);
      clearLiveReports();
      clearMessages();

      const res = await axios.get('/api/reports/pending-inventory-costs', getAuthHeaders());

      setPendingInventoryCostsReport(res.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to load Pending Inventory Costs report.'
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const handleRunInventoryReport = async () => {
    if (exportType === 'top_inventory_cost') {
      fetchInventoryCostReport(topLimit);
      return;
    }

    if (exportType === 'total_inventory_cost') {
      fetchTotalInventoryCost();
      return;
    }

    if (exportType === 'quick_buy_value_report') {
      fetchQuickBuyReport();
      return;
    }

    if (exportType === 'pending_inventory_costs') {
      fetchPendingInventoryCosts();
      return;
    }

    const link = document.createElement('a');

    switch (exportType) {
      case 'inventory_costs':
        link.href = '/api/tools/export-costs';
        link.setAttribute('download', 'inventory_costs.csv');
        break;

      case 'annual_inventory':
        link.href = '/api/reports/annual-inventory';
        link.setAttribute('download', 'annual_inventory.csv');
        break;

      case 'quarterly_sales': {
        const params = new URLSearchParams();

        if (startDate) params.append('start', startDate);
        if (endDate) params.append('end', endDate);

        link.href = `/api/reports/quarterly-sales?${params.toString()}`;
        link.setAttribute('download', 'quarterly_sales.csv');
        break;
      }

      default:
        setError('Please select a valid report type.');
        return;
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (reportType === 'Discards') {
      fetchDiscards();
    } else if (reportType === 'Giveaways') {
      fetchGiveaways();
    } else if (reportType === 'eBay Fees') {
      fetchEbayFees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const MiniMetric = ({ label, value, color = '#e5e7eb', subtext }) => {
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 16,
          background: 'rgba(2, 6, 23, 0.45)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
        }}
      >
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 3 }}>{label}</div>
        <div style={{ color, fontWeight: 850 }}>{value}</div>
        {subtext && <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>{subtext}</div>}
      </div>
    );
  };

  const StatCard = ({ label, value, subtext, accent = '#38bdf8', icon = '📊', valueColor }) => {
    return (
      <div className="col-md-6 col-xl-3 mb-3">
        <div
          className="h-100"
          style={{
            ...cardStyle,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at top right, ${accent}33, transparent 36%)`,
              pointerEvents: 'none',
            }}
          />

          <div className="p-3 p-lg-4 position-relative">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div
                  style={{
                    color: '#94a3b8',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}
                >
                  {label}
                </div>

                <div
                  className="mt-2"
                  style={{
                    fontSize: 30,
                    lineHeight: 1.05,
                    fontWeight: 900,
                    color: valueColor || '#f8fafc',
                  }}
                >
                  {value}
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 16,
                  background: `${accent}22`,
                  border: `1px solid ${accent}35`,
                  fontSize: 22,
                }}
              >
                {icon}
              </div>
            </div>

            {subtext && (
              <div className="mt-2" style={{ color: '#94a3b8', fontSize: 13 }}>
                {subtext}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ title, subtitle, action }) => {
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 850 }}>
            {title}
          </h4>
          {subtitle && <p className="mb-0" style={mutedStyle}>{subtitle}</p>}
        </div>
        {action}
      </div>
    );
  };

  const ReportNavItem = ({ item }) => {
    const active = reportType === item.value;

    return (
      <button
        type="button"
        onClick={() => {
          setReportType(item.value);
          clearMessages();
          clearLiveReports();
          clearGiveawayEdit();
        }}
        style={{
          width: '100%',
          textAlign: 'left',
          borderRadius: 18,
          border: active ? `1px solid ${item.color}66` : '1px solid rgba(148, 163, 184, 0.12)',
          background: active ? `${item.color}1f` : 'rgba(2, 6, 23, 0.35)',
          color: '#e5e7eb',
          padding: 13,
          marginBottom: 10,
          transition: 'all 160ms ease',
        }}
      >
        <div className="d-flex align-items-start gap-3">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 38,
              height: 38,
              borderRadius: 14,
              background: `${item.color}22`,
              border: `1px solid ${item.color}35`,
              fontSize: 18,
            }}
          >
            {item.icon}
          </div>

          <div>
            <div style={{ color: '#f8fafc', fontWeight: 900, lineHeight: 1.2 }}>
              {item.label}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 3 }}>
              {item.description}
            </div>
          </div>
        </div>
      </button>
    );
  };

  const ReportMetricGrid = ({ metrics }) => {
    return (
      <Row className="g-3">
        {metrics.map((metric, index) => (
          <Col md={3} key={`${metric.label}-${index}`}>
            <MiniMetric
              label={metric.label}
              value={metric.value}
              color={metric.color}
              subtext={metric.subtext}
            />
          </Col>
        ))}
      </Row>
    );
  };

  const EmptyTable = ({ colSpan, text = 'No records found.' }) => (
    <tr>
      <td colSpan={colSpan} className="text-center py-5">
        <div style={{ color: '#f8fafc', fontWeight: 900, fontSize: 18 }}>{text}</div>
        <div style={mutedStyle}>Try changing filters or running the report again.</div>
      </td>
    </tr>
  );

  const renderInventoryReports = () => (
    <>
      <SectionHeader
        title="Inventory & Cost Reports"
        subtitle="Run live inventory value reports or download CSV reports."
        action={
          liveReportLoaded && (
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => {
                clearLiveReports();
                clearMessages();
              }}
            >
              Clear Results
            </Button>
          )
        }
      />

      <div className="p-3 mb-4" style={cardSoftStyle}>
        <Row className="align-items-end g-3">
          <Col lg={4} md={6}>
            <Form.Label>Report</Form.Label>
            <Form.Select
              style={inputStyle}
              value={exportType}
              onChange={(e) => {
                setExportType(e.target.value);
                clearLiveReports();
                clearMessages();
              }}
            >
              <option value="inventory_costs">Inventory Costs (CSV)</option>
              <option value="annual_inventory">Annual Inventory Report (CSV)</option>
              <option value="quarterly_sales">Quarterly Sales Report (CSV)</option>
              <option value="top_inventory_cost">Top Inventory Cost (Live)</option>
              <option value="total_inventory_cost">Total Inventory Cost (Live)</option>
              <option value="quick_buy_value_report">Quick Buy Value Report (Live)</option>
              <option value="pending_inventory_costs">Pending Inventory Costs (Live)</option>
            </Form.Select>
          </Col>

          {exportType === 'quarterly_sales' && (
            <>
              <Col lg={2} md={3}>
                <Form.Label>Start</Form.Label>
                <Form.Control
                  style={inputStyle}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>

              <Col lg={2} md={3}>
                <Form.Label>End</Form.Label>
                <Form.Control
                  style={inputStyle}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
            </>
          )}

          {exportType === 'top_inventory_cost' && (
            <Col lg={3} md={4}>
              <Form.Label>Limit</Form.Label>
              <Form.Select
                style={inputStyle}
                value={topLimit}
                onChange={(e) => setTopLimit(Number(e.target.value))}
              >
                <option value={25}>Top 25</option>
                <option value={50}>Top 50</option>
                <option value={100}>Top 100</option>
              </Form.Select>
            </Col>
          )}

          <Col lg={3} md={4}>
            <Button
              variant="success"
              onClick={handleRunInventoryReport}
              disabled={loadingReport}
              className="w-100"
            >
              {loadingReport ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Loading…
                </>
              ) : (
                'Run Report'
              )}
            </Button>
          </Col>
        </Row>
      </div>

      {loadingReport && (
        <div className="text-center p-5" style={cardSoftStyle}>
          <Spinner animation="border" variant="info" />
          <div className="mt-3" style={mutedStyle}>
            Loading report...
          </div>
        </div>
      )}

      {totalInventoryCost !== null && (
        <div className="p-4 mt-4" style={cardStyle}>
          <SectionHeader
            title="Total Inventory Cost"
            subtitle="Total cost currently returned by the inventory cost report."
          />

          <div
            style={{
              color: '#f8fafc',
              fontSize: 'clamp(2.8rem, 6vw, 5.5rem)',
              lineHeight: 1,
              fontWeight: 950,
              letterSpacing: '-0.05em',
            }}
          >
            {formatMoney(totalInventoryCost)}
          </div>
        </div>
      )}

      {quickBuyReport && (
        <div className="p-4 mt-4" style={cardStyle}>
          <SectionHeader
            title="Quick Buy Value Report"
            subtitle="Active products currently inside your Quick Buy list."
            action={
              <Button
                variant="outline-info"
                size="sm"
                onClick={fetchQuickBuyReport}
                disabled={loadingReport}
              >
                Refresh
              </Button>
            }
          />

          <ReportMetricGrid
            metrics={[
              {
                label: 'Quick Buy SKUs',
                value: toNumber(quickBuyReport.total_skus).toLocaleString(),
                color: '#38bdf8',
              },
              {
                label: 'Total Units',
                value: toNumber(quickBuyReport.total_units).toLocaleString(),
                color: '#c4b5fd',
              },
              {
                label: 'Total COGS',
                value: formatMoney(quickBuyReport.total_cogs),
                color: '#fbbf24',
              },
              {
                label: 'Value If Sold',
                value: formatMoney(quickBuyReport.total_value_if_sold),
                color: '#86efac',
              },
            ]}
          />

          <div
            className="mt-3 p-3"
            style={{
              borderRadius: 18,
              background: 'rgba(2, 6, 23, 0.45)',
              border: '1px solid rgba(148, 163, 184, 0.12)',
            }}
          >
            <div style={{ color: '#94a3b8', fontSize: 12 }}>
              Potential Gross Profit
            </div>
            <div style={{ color: quickBuyProfitColor, fontWeight: 950, fontSize: 32 }}>
              {formatMoney(quickBuyReport.gross_profit_if_sold)}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>
              Formula: current product price × stock, minus COGS × stock.
            </div>
          </div>
        </div>
      )}

      {pendingInventoryCostsReport && (
        <div className="p-4 mt-4" style={cardStyle}>
          <SectionHeader
            title="Pending Inventory Costs"
            subtitle="FIFO/cost batches that are still pending and not released into active inventory."
            action={
              <Button
                variant="outline-info"
                size="sm"
                onClick={fetchPendingInventoryCosts}
                disabled={loadingReport}
              >
                Refresh
              </Button>
            }
          />

          <ReportMetricGrid
            metrics={[
              {
                label: 'Pending SKUs',
                value: toNumber(pendingInventoryCostsReport.total_skus).toLocaleString(),
                color: '#38bdf8',
              },
              {
                label: 'Pending Units',
                value: toNumber(pendingInventoryCostsReport.total_units).toLocaleString(),
                color: '#c4b5fd',
              },
              {
                label: 'Pending COGS',
                value: formatMoney(pendingInventoryCostsReport.total_cogs),
                color: '#fbbf24',
              },
              {
                label: 'Value If Sold',
                value: formatMoney(pendingInventoryCostsReport.total_value_if_sold),
                color: '#86efac',
              },
            ]}
          />

          <div
            className="mt-3 p-3"
            style={{
              borderRadius: 18,
              background: 'rgba(2, 6, 23, 0.45)',
              border: '1px solid rgba(148, 163, 184, 0.12)',
            }}
          >
            <div style={{ color: '#94a3b8', fontSize: 12 }}>
              Potential Gross Profit
            </div>
            <div style={{ color: pendingProfitColor, fontWeight: 950, fontSize: 32 }}>
              {formatMoney(pendingInventoryCostsReport.gross_profit_if_sold)}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>
              Formula: current product price × pending quantity, minus pending COGS.
            </div>
          </div>

          {(pendingInventoryCostsReport.items || []).length > 0 && (
            <div className="mt-4">
              <h5 className="mb-3" style={{ color: '#f8fafc', fontWeight: 900 }}>
                Pending Inventory Items
              </h5>

              <div className="table-responsive" style={{ maxHeight: 450, overflowY: 'auto' }}>
                <Table hover responsive size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Item</th>
                      <th className="text-end">Pending Qty</th>
                      <th className="text-end">Unit Cost</th>
                      <th className="text-end">Price</th>
                      <th className="text-end">Pending COGS</th>
                      <th className="text-end">Value If Sold</th>
                      <th className="text-end">Gross Profit</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(pendingInventoryCostsReport.items || []).map((row) => (
                      <tr key={`${row.id}-${row.sku}`}>
                        <td>
                          <strong style={{ color: '#f8fafc' }}>{row.sku}</strong>
                        </td>
                        <td style={{ minWidth: 260, whiteSpace: 'normal' }}>{row.name}</td>
                        <td className="text-end">{toNumber(row.remaining_qty).toLocaleString()}</td>
                        <td className="text-end">{formatMoney(row.purchase_cost)}</td>
                        <td className="text-end">{formatMoney(row.price)}</td>
                        <td className="text-end">{formatMoney(row.pending_cogs)}</td>
                        <td className="text-end">{formatMoney(row.value_if_sold)}</td>
                        <td className="text-end">
                          <strong style={{ color: getMoneyColor(row.gross_profit_if_sold) }}>
                            {formatMoney(row.gross_profit_if_sold)}
                          </strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}

      {inventoryReport && (
        <div className="p-4 mt-4" style={cardStyle}>
          <SectionHeader
            title={`Top ${inventoryReport.count} SKUs by Inventory Cost`}
            subtitle={`${formatMoney(inventoryReport.top_total_cost)} total cost · ${inventoryReport.percent_of_inventory}% of inventory`}
          />

          <div className="table-responsive" style={{ maxHeight: 450, overflowY: 'auto' }}>
            <Table hover responsive size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Item</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Unit Cost</th>
                  <th className="text-end">Total Cost</th>
                </tr>
              </thead>

              <tbody>
                {(inventoryReport.items || []).map((row) => (
                  <tr key={row.sku}>
                    <td>
                      <strong style={{ color: '#f8fafc' }}>{row.sku}</strong>
                    </td>
                    <td style={{ minWidth: 260, whiteSpace: 'normal' }}>{row.name}</td>
                    <td className="text-end">{toNumber(row.quantity).toLocaleString()}</td>
                    <td className="text-end">{formatMoney(row.unit_cost)}</td>
                    <td className="text-end">
                      <strong style={{ color: '#fbbf24' }}>{formatMoney(row.total_cost)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </>
  );

  const renderGiveaways = () => (
    <>
      <SectionHeader
        title="Giveaway Write-Offs"
        subtitle="Record promotional giveaway inventory, order numbers, and manual shipping costs for accounting."
        action={
          <div className="d-flex flex-wrap gap-2">
            <Button variant="outline-info" onClick={fetchGiveaways} disabled={giveawaysLoading}>
              Refresh
            </Button>

            <Button variant="outline-danger" onClick={handleExportGiveaways}>
              Export CSV
            </Button>

            <Button variant="secondary" onClick={handleArchiveGiveaways}>
              Archive & Reset
            </Button>
          </div>
        }
      />

      <div className="p-3 mb-4" style={cardSoftStyle}>
        <Row className="align-items-end g-3">
          <Col lg={3} md={6}>
            <Form.Label>SKU</Form.Label>
            <Form.Control
              style={inputStyle}
              value={giveawaySku}
              onChange={(e) => setGiveawaySku(e.target.value)}
              placeholder="Optional for shipping-only"
              autoCapitalize="characters"
            />
          </Col>

          <Col lg={2} md={3}>
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              style={inputStyle}
              type="number"
              min="1"
              value={giveawayQuantity}
              onChange={(e) => setGiveawayQuantity(e.target.value)}
            />
          </Col>

          <Col lg={3} md={6}>
            <Form.Label>Event / Stream</Form.Label>
            <Form.Control
              style={inputStyle}
              value={giveawayEventName}
              onChange={(e) => setGiveawayEventName(e.target.value)}
              placeholder="TikTok Live, YouTube, Whatnot..."
            />
          </Col>

          <Col lg={2} md={4}>
            <Form.Label>Order Number</Form.Label>
            <Form.Control
              style={inputStyle}
              value={giveawayOrderNumber}
              onChange={(e) => setGiveawayOrderNumber(e.target.value)}
              placeholder="Order # if needed"
            />
          </Col>

          <Col lg={2} md={4}>
            <Form.Label>Shipping Cost</Form.Label>
            <Form.Control
              style={inputStyle}
              type="number"
              min="0"
              step="0.01"
              value={giveawayShippingCost}
              onChange={(e) => setGiveawayShippingCost(e.target.value)}
              placeholder="0.00"
            />
          </Col>

          <Col lg={4} md={8}>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              style={inputStyle}
              value={giveawayNotes}
              onChange={(e) => setGiveawayNotes(e.target.value)}
              placeholder="Winner, reason, order note..."
            />
          </Col>

          <Col lg={3} md={4}>
            <Button
              variant="success"
              onClick={handleAddGiveaway}
              disabled={giveawaysLoading}
              className="w-100"
            >
              Add Giveaway Write-Off
            </Button>
          </Col>
        </Row>
      </div>

      <Row className="mb-3">
        <Col md={3}>
          <MiniMetric
            label="Giveaway Records"
            value={giveawayTotals.count.toLocaleString()}
            color="#f9a8d4"
          />
        </Col>

        <Col md={3}>
          <MiniMetric
            label="Units Given Away"
            value={giveawayTotals.quantity.toLocaleString()}
            color="#c4b5fd"
          />
        </Col>

        <Col md={3}>
          <MiniMetric
            label="Inventory Cost"
            value={formatMoney(giveawayTotals.cost)}
            color="#f9a8d4"
          />
        </Col>

        <Col md={3}>
          <MiniMetric
            label="Shipping Cost"
            value={formatMoney(giveawayTotals.shipping)}
            color="#fbbf24"
            subtext={`Total write-off ${formatMoney(giveawayTotals.total)}`}
          />
        </Col>
      </Row>

      {giveawaysLoading ? (
        <div className="text-center p-5" style={cardSoftStyle}>
          <Spinner animation="border" variant="info" />
          <div className="mt-3" style={mutedStyle}>
            Loading giveaway write-offs...
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Title</th>
                <th className="text-end">Qty</th>
                <th className="text-end">Unit Cost</th>
                <th className="text-end">Inventory Cost</th>
                <th className="text-end">Shipping</th>
                <th className="text-end">Total Write-Off</th>
                <th>Order #</th>
                <th>Event</th>
                <th>Notes</th>
                <th>Date</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>

            <tbody>
              {giveaways.length > 0 ? (
                giveaways.map((item) => {
                  const isEditing = editingGiveawayId === item.id;
                  const itemInventoryCost = toNumber(item.total_cost);
                  const itemShippingCost = isEditing
                    ? toNumber(editGiveawayShippingCost)
                    : toNumber(item.shipping_cost);
                  const itemTotalWriteoff = itemInventoryCost + itemShippingCost;

                  return (
                    <tr key={item.id}>
                      <td>
                        <strong style={{ color: '#f8fafc' }}>{item.sku || 'Shipping Only'}</strong>
                      </td>

                      <td style={{ minWidth: 260, whiteSpace: 'normal' }}>{item.name}</td>

                      <td className="text-end">{toNumber(item.quantity).toLocaleString()}</td>

                      <td className="text-end">{formatMoney(item.unit_cost)}</td>

                      <td className="text-end">
                        <strong style={{ color: '#f9a8d4' }}>
                          {formatMoney(item.total_cost)}
                        </strong>
                      </td>

                      <td className="text-end">
                        {isEditing ? (
                          <Form.Control
                            style={inlineInputStyle}
                            type="number"
                            min="0"
                            step="0.01"
                            value={editGiveawayShippingCost}
                            onChange={(e) => setEditGiveawayShippingCost(e.target.value)}
                          />
                        ) : (
                          formatMoney(item.shipping_cost)
                        )}
                      </td>

                      <td className="text-end">
                        <strong style={{ color: '#fbbf24' }}>
                          {formatMoney(itemTotalWriteoff)}
                        </strong>
                      </td>

                      <td>
                        {isEditing ? (
                          <Form.Control
                            style={inlineInputStyle}
                            value={editGiveawayOrderNumber}
                            onChange={(e) => setEditGiveawayOrderNumber(e.target.value)}
                            placeholder="Order #"
                          />
                        ) : (
                          item.order_number || '-'
                        )}
                      </td>

                      <td>{item.event_name || '-'}</td>

                      <td style={{ minWidth: 220, whiteSpace: 'normal' }}>{item.notes || '-'}</td>

                      <td>{new Date(item.created_at).toLocaleString()}</td>

                      <td className="text-end">
                        {isEditing ? (
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleSaveGiveawayEdit(item)}
                              disabled={savingGiveawayEdit}
                            >
                              {savingGiveawayEdit ? 'Saving...' : 'Save'}
                            </Button>

                            <Button
                              variant="outline-light"
                              size="sm"
                              onClick={clearGiveawayEdit}
                              disabled={savingGiveawayEdit}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleStartGiveawayEdit(item)}
                          >
                            Edit Shipping
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <EmptyTable colSpan={12} text="No giveaway write-offs." />
              )}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );

  const renderDiscards = () => (
    <>
      <SectionHeader
        title="Discarded Items"
        subtitle="Items removed from inventory as damaged, discarded, or otherwise written off."
        action={
          <div className="d-flex flex-wrap gap-2">
            <Button variant="outline-danger" onClick={handleExportDiscards}>
              Export CSV
            </Button>

            <Button variant="secondary" onClick={handleArchiveDiscards}>
              Archive & Reset
            </Button>
          </div>
        }
      />

      <Row className="mb-3">
        <Col md={6}>
          <MiniMetric
            label="Discarded Items"
            value={discardTotals.count.toLocaleString()}
            color="#fca5a5"
          />
        </Col>
        <Col md={6}>
          <MiniMetric
            label="Discarded Cost"
            value={formatMoney(discardTotals.cost)}
            color="#fca5a5"
          />
        </Col>
      </Row>

      {loading ? (
        <div className="text-center p-5" style={cardSoftStyle}>
          <Spinner animation="border" variant="danger" />
          <div className="mt-3" style={mutedStyle}>Loading discards...</div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Title</th>
                <th className="text-end">Cost</th>
                <th>Reason</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              {discards.length > 0 ? (
                discards.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong style={{ color: '#f8fafc' }}>{item.sku}</strong>
                    </td>
                    <td style={{ minWidth: 260, whiteSpace: 'normal' }}>{item.name}</td>
                    <td className="text-end">
                      {item.cost !== null && item.cost !== undefined
                        ? formatMoney(item.cost)
                        : '-'}
                    </td>
                    <td>{item.reason}</td>
                    <td>{new Date(item.created_at).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <EmptyTable colSpan={5} text="No discarded items." />
              )}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );

  const renderEbayFees = () => (
    <>
      <SectionHeader
        title="eBay Fees Report"
        subtitle="View and export eBay fee transactions by date range."
        action={
          <Button
            variant="outline-danger"
            onClick={() =>
              window.open(`/api/ebay/fees/export?start=${startDate}&end=${endDate}`, '_blank')
            }
          >
            Export Fees CSV
          </Button>
        }
      />

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Button
          variant="outline-secondary"
          onClick={() => {
            const start = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
            const end = new Date().toISOString().split('T')[0];

            setStartDate(start);
            setEndDate(end);
          }}
        >
          Last 7 Days
        </Button>

        <Button
          variant="outline-secondary"
          onClick={() => {
            const start = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
            const end = new Date().toISOString().split('T')[0];

            setStartDate(start);
            setEndDate(end);
          }}
        >
          Last 30 Days
        </Button>

        <Button
          variant="outline-secondary"
          onClick={() => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth(), 1)
              .toISOString()
              .split('T')[0];
            const end = new Date().toISOString().split('T')[0];

            setStartDate(start);
            setEndDate(end);
          }}
        >
          This Month
        </Button>
      </div>

      <div className="p-3 mb-4" style={cardSoftStyle}>
        <Row className="align-items-end g-3">
          <Col md={3}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              style={inputStyle}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              style={inputStyle}
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Button
              variant="primary"
              onClick={fetchEbayFees}
              disabled={feesLoading}
              className="w-100"
            >
              {feesLoading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Loading...
                </>
              ) : (
                'Load Fees'
              )}
            </Button>
          </Col>
        </Row>
      </div>

      <Row className="mb-3">
        <Col md={6}>
          <MiniMetric
            label="Fee Transactions"
            value={feeTotals.count.toLocaleString()}
            color="#93c5fd"
          />
        </Col>
        <Col md={6}>
          <MiniMetric
            label="Total Fee Amount"
            value={formatMoney(feeTotals.amount)}
            color={getMoneyColor(feeTotals.amount)}
          />
        </Col>
      </Row>

      {feesLoading ? (
        <div className="text-center p-5" style={cardSoftStyle}>
          <Spinner animation="border" variant="primary" />
          <div className="mt-3" style={mutedStyle}>Loading eBay fees...</div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th className="text-end">Amount</th>
                <th>Memo</th>
                <th>Reference</th>
              </tr>
            </thead>

            <tbody>
              {fees.length > 0 ? (
                fees.map((fee, i) => (
                  <tr key={i}>
                    <td>{new Date(fee.date).toLocaleString()}</td>
                    <td>
                      <Badge
                        bg=""
                        style={{
                          background: 'rgba(59,130,246,0.14)',
                          color: '#bfdbfe',
                          border: '1px solid rgba(59,130,246,0.25)',
                          borderRadius: 999,
                        }}
                      >
                        {fee.type}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <strong style={{ color: getMoneyColor(fee.amount) }}>
                        {formatMoney(fee.amount)}
                      </strong>
                    </td>
                    <td style={{ minWidth: 260, whiteSpace: 'normal' }}>{fee.memo}</td>
                    <td>{fee.reference || '-'}</td>
                  </tr>
                ))
              ) : (
                <EmptyTable colSpan={5} text="No fees found." />
              )}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );

  const renderPlaceholder = (title, text, icon = '🚧') => (
    <div
      className="p-5 text-center"
      style={{
        borderRadius: 22,
        background: 'rgba(2, 6, 23, 0.35)',
        border: '1px solid rgba(148, 163, 184, 0.12)',
      }}
    >
      <div style={{ fontSize: 46, marginBottom: 12 }}>{icon}</div>
      <h4 style={{ color: '#f8fafc', fontWeight: 900 }}>{title}</h4>
      <p style={mutedStyle} className="mb-0">{text}</p>
    </div>
  );

  const renderReport = () => {
    switch (reportType) {
      case 'Inventory Reports':
        return renderInventoryReports();

      case 'Sales':
        return renderPlaceholder('Sales Report', 'Sales report coming soon…', '📈');

      case 'Inventory':
        return renderPlaceholder('Inventory Report', 'Inventory report coming soon…', '🏷️');

      case 'Orders':
        return renderPlaceholder('Orders Report', 'Orders report coming soon…', '🧾');

      case 'Discards':
        return renderDiscards();

      case 'Giveaways':
        return renderGiveaways();

      case 'eBay Fees':
        return renderEbayFees();

      default:
        return <p>Select a report type.</p>;
    }
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          .reports-dark input,
          .reports-dark select,
          .reports-dark textarea {
            background-color: #020617 !important;
            color: #e5e7eb !important;
            border-color: rgba(148, 163, 184, 0.35) !important;
          }

          .reports-dark input::placeholder,
          .reports-dark textarea::placeholder {
            color: #64748b !important;
          }

          .reports-dark input:focus,
          .reports-dark select:focus,
          .reports-dark textarea:focus {
            box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.18) !important;
            border-color: rgba(56, 189, 248, 0.75) !important;
          }

          .reports-dark .form-label {
            color: #cbd5e1;
            font-size: 13px;
            font-weight: 700;
          }

          .reports-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .reports-dark .table {
            --bs-table-bg: transparent;
            --bs-table-color: #e5e7eb;
            --bs-table-border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
            margin-bottom: 0;
          }

          .reports-dark .table thead th {
            color: #cbd5e1;
            background: rgba(15, 23, 42, 0.94) !important;
            border-bottom: 1px solid rgba(148, 163, 184, 0.18);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            white-space: nowrap;
            vertical-align: middle;
          }

          .reports-dark .table tbody td {
            vertical-align: middle;
            border-color: rgba(148, 163, 184, 0.10);
            white-space: nowrap;
          }

          .reports-dark .table tbody tr:hover {
            background: rgba(30, 41, 59, 0.48);
          }
        `}
      </style>

      <Container fluid className="py-4 reports-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
                  Reports
                </span>
                <span style={{ ...pillStyle, color: selectedReportMeta.color, borderColor: `${selectedReportMeta.color}55` }}>
                  {selectedReportMeta.icon} {selectedReportMeta.label}
                </span>
                <span style={{ ...pillStyle, color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.38)' }}>
                  CSV + Live Reports
                </span>
                <span style={{ ...pillStyle, color: '#fef3c7', borderColor: 'rgba(245,158,11,0.38)' }}>
                  COGS + Inventory Value
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.04em',
                  fontSize: 'clamp(2rem, 4vw, 4.35rem)',
                  lineHeight: 0.95,
                }}
              >
                Reports Control Center
              </h1>

              <p className="mb-0" style={{ color: '#cbd5e1', maxWidth: 940, fontSize: 17 }}>
                Inventory value, COGS, pending inventory, Quick Buy reports, discarded items,
                eBay fees, and export tools in one clean reporting page.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 320,
              }}
            >
              <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Current Report
              </div>

              <div className="mt-1 mb-3" style={{ color: '#f8fafc', fontSize: 26, fontWeight: 900 }}>
                {selectedReportMeta.icon} {selectedReportMeta.label}
              </div>

              <Row className="g-2">
                <Col xs={6}>
                  <MiniMetric label="Date Start" value={startDate} color="#38bdf8" />
                </Col>
                <Col xs={6}>
                  <MiniMetric label="Date End" value={endDate} color="#c4b5fd" />
                </Col>
              </Row>
            </div>
          </div>
        </div>

        {message && (
          <Alert
            style={{
              background: 'rgba(22,101,52,0.70)',
              border: '1px solid rgba(74,222,128,0.35)',
              color: '#dcfce7',
              borderRadius: 18,
            }}
          >
            {message}
          </Alert>
        )}

        {error && (
          <Alert
            style={{
              background: 'rgba(127,29,29,0.70)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fee2e2',
              borderRadius: 18,
            }}
          >
            {error}
          </Alert>
        )}

        <Row className="mb-4">
          <StatCard
            label="Quick Buy"
            value={quickBuyReport ? toNumber(quickBuyReport.total_skus).toLocaleString() : 'Ready'}
            subtext="Live Quick Buy value report"
            accent="#38bdf8"
            icon="⚡"
          />

          <StatCard
            label="Pending Inventory"
            value={pendingInventoryCostsReport ? toNumber(pendingInventoryCostsReport.total_skus).toLocaleString() : 'Ready'}
            subtext="Pending FIFO/cost batches"
            accent="#f59e0b"
            icon="⏳"
          />

          <StatCard
            label="Giveaways"
            value={giveawayTotals.count.toLocaleString()}
            subtext={`${formatMoney(giveawayTotals.total)} total incl. shipping`}
            accent="#ec4899"
            icon="🎁"
            valueColor="#f9a8d4"
          />

          <StatCard
            label="Discards"
            value={discardTotals.count.toLocaleString()}
            subtext={`${formatMoney(discardTotals.cost)} written off`}
            accent="#ef4444"
            icon="🗑️"
            valueColor="#fca5a5"
          />

          <StatCard
            label="eBay Fees"
            value={feeTotals.count.toLocaleString()}
            subtext={`${formatMoney(feeTotals.amount)} in loaded fees`}
            accent="#3b82f6"
            icon="💳"
            valueColor="#93c5fd"
          />
        </Row>

        <Row>
          <Col xl={3} lg={4} className="mb-4">
            <div className="p-4 h-100" style={cardStyle}>
              <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                Select Report
              </h4>
              <p className="mb-3" style={mutedStyle}>
                Pick the report area you want to run.
              </p>

              {reportOptions.map((item) => (
                <ReportNavItem key={item.value} item={item} />
              ))}
            </div>
          </Col>

          <Col xl={9} lg={8} className="mb-4">
            <div className="p-4" style={cardStyle}>
              {renderReport()}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Reports;